import menuitems from './menuitems';

// Maps an admin page to its standalone user-guide HTML file (served from /public/docs).
//
// Pages are keyed by their menu entry's `text` in menuitems.js, which is unique across the menu.
// The displayed page title is NOT always that text: the in-app sidebar stores `linktext ?? text`
// as the title (submenubutton_click), while the F5/deep-link path (content.js load) stores
// `text`. getUserGuidePath therefore resolves the incoming title back to its menu entry (matching
// text and linktext alike, using the click url to disambiguate entries that share a display
// label, e.g. 'Invoices' on Client Invoices and Vendor - Invoices) and looks the guide up by the
// entry's `text`. Titles set directly via link_click on pages outside the menu tree fall back to
// a plain title lookup, so such pages can be keyed by their dispatched title.
//
// Only pages listed here make their header title clickable to open the guide (rendered in
// content.js); pages without a guide yet keep a plain title, so coverage can be rolled out safely
// without broken links.
//
// Values are paths under the app's public folder (relative to PUBLIC_URL).
const USER_GUIDES = {
  // Landing page & account
  Home: 'docs/user-guides/home.html',
  Profile: 'docs/user-guides/profile.html',

  // Third-party transfer
  'Survey Transfer Mapping': 'docs/survey-transfer-mapping-guide.html',
  'Apollo Shop Copier': 'docs/user-guides/apollo-shop-copier.html',

  // Sales
  Leads: 'docs/user-guides/leads.html',
  Clients: 'docs/user-guides/clients.html',
  Contacts: 'docs/user-guides/contacts.html',
  Opportunities: 'docs/user-guides/opportunities.html',
  Services: 'docs/user-guides/services.html',
  'Commission Line': 'docs/user-guides/commission-lines.html',

  // Operations
  Planning: 'docs/user-guides/planning.html',
  Locations: 'docs/user-guides/locations.html',
  Assignments: 'docs/user-guides/assignments.html',
  Projects: 'docs/user-guides/projects.html',
  'Payment Exports': 'docs/user-guides/payment-exports.html',
  'Credit Memos': 'docs/user-guides/credit-memos.html',
  'Assignment Payments': 'docs/user-guides/assignment-payments.html',
  IOB: 'docs/user-guides/iob.html',
  'Evaluator Admin': 'docs/user-guides/evaluators.html',
  'Client Invoices': 'docs/user-guides/client-invoices.html',
  'Anti-Fraud Tool': 'docs/user-guides/anti-fraud-tool.html',

  // Finance
  'Client Payments': 'docs/user-guides/client-payments.html',
  'Client Payments (New)': 'docs/user-guides/client-payments-new.html',
  'FTE Tracking': 'docs/user-guides/fte-tracking.html',

  // Third-party operations
  'TP Evaluator': 'docs/user-guides/tp-evaluators.html',
  'TP Jobs': 'docs/user-guides/tp-jobs.html',
  'TP Clients': 'docs/user-guides/tp-clients.html',
  TpLocation: 'docs/user-guides/tp-locations.html',
  'TP Export Excluded Items': 'docs/user-guides/tp-export-excluded-items.html',
  'TP Import Excluded Items': 'docs/user-guides/tp-import-excluded-items.html',
  'Third-Party Projects': 'docs/user-guides/tp-projects.html',
  'Third-Party Client Invoices': 'docs/user-guides/tp-client-invoices.html',
  'Third-Party Credit Memos': 'docs/user-guides/tp-credit-memos.html',
  'TP Platform Invoices': 'docs/user-guides/tp-platform-invoices.html',
  'TP Evaluator TP Job Payments': 'docs/user-guides/tp-evaluator-job-payments.html',

  // Apollo API job import & tools
  'Apollo API JobImport': 'docs/user-guides/import-shops.html',
  ImportShops: 'docs/user-guides/import-shops.html',
  'Apollo Data Export': 'docs/user-guides/apollo-data-export.html',
  CaspianXx: 'docs/user-guides/caspianxx.html',
  'Apollo Custom Trigger': 'docs/user-guides/apollo-custom-trigger.html',
  'BambooHR Api Log': 'docs/user-guides/bamboohr-api-log.html',

  // Administration & reference
  User: 'docs/user-guides/users.html',
  Offices: 'docs/user-guides/offices.html',
  'Office General': 'docs/user-guides/office-general.html',
  Countries: 'docs/user-guides/countries.html',
  Currency: 'docs/user-guides/currency.html',
  'System Alerts': 'docs/user-guides/system-alerts.html',
  'End of Month': 'docs/user-guides/end-of-month.html',
  'User Groups': 'docs/user-guides/user-groups.html',
  Dashboard: 'docs/user-guides/dashboard.html',
  Lookup: 'docs/user-guides/lookup.html',
  'Url Shortener': 'docs/user-guides/url-shortener.html',

  // Vendor (freelancer) invoicing
  'Vendor Invoicing': 'docs/user-guides/vendor-users.html',
  'Vendor - Users': 'docs/user-guides/vendor-users.html',
  'Vendor - Fees': 'docs/user-guides/vendor-fees.html',
  'Vendor - Line Items': 'docs/user-guides/vendor-line-items.html',
  'Vendor - Payable Assignments': 'docs/user-guides/vendor-payable-assignments.html',
  'Vendor - Invoices': 'docs/user-guides/vendor-invoices.html',
  'Vendor - Payment Exports': 'docs/user-guides/vendor-payment-exports.html',
  'Vendor Dashboard': 'docs/user-guides/vendor-dashboard.html',

  // Activity tracker (timesheets)
  'Activity Tracker': 'docs/user-guides/activity-tracker.html',
  'Manage tasks': 'docs/user-guides/manage-tasks.html',
  Export: 'docs/user-guides/activity-export.html',
  'Availability Planner': 'docs/availability-planner-guide.html',
  'Office Calendar': 'docs/availability-planner-guide.html',

  // Tools & utilities
  'AI Tools': 'docs/user-guides/ai-chat.html',
  'AI Chat': 'docs/user-guides/ai-chat.html',
  Messages: 'docs/user-guides/messages.html',
  Downloads: 'docs/user-guides/downloads.html',

  // Reports (the launcher pages have their own guides; the overview covers the rest of the reporting area)
  Reports: 'docs/user-guides/reports.html',
  'OPS Reports': 'docs/user-guides/ops-reports.html',
  'General Finance Reports': 'docs/user-guides/general-finance-reports.html',
  'Finance Reports': 'docs/user-guides/finance-reports.html',
  'Corporate Office Finance Rep.': 'docs/user-guides/corporate-office-finance-reports.html',
  'Revenue Metrics Reports': 'docs/user-guides/revenue-metrics-reports.html',
  'Shop Count Report': 'docs/user-guides/reports.html',
  'Ops PBI Dashboard': 'docs/user-guides/reports.html',
  'DQ TAT Report': 'docs/user-guides/reports.html',
  // Testing (BI test audience)
  'BI Test Reports': 'docs/user-guides/bi-test-reports.html',
  'Domo Open Shops Report': 'docs/user-guides/domo-open-shops-report.html',
};

// Url-keyed guides for the URL-only pages in the hidden "Don't show up" bucket. Their titles
// cannot be used as keys: detail pages embed the record id in the header (getMenuText() yields
// e.g. 'Client Payment - 12345'), so the stable identity of such a page is its url slug. A
// detail page opens the guide of the list page it belongs to, unless it has a guide of its own.
// Keys are lowercase (url matching is case-insensitive because the F5 loader compares lowercased slugs).
export const USER_GUIDES_BY_URL = {
  paymentexportdetails: 'docs/user-guides/payment-export-details.html',
  planning_planninglist: 'docs/user-guides/planning.html',
  planning_planningdetaillist: 'docs/user-guides/planning.html',
  clientlineitemdetails: 'docs/user-guides/client-line-details.html',
  tpclientinvoicelineitemdetails: 'docs/user-guides/tp-credit-memos.html',
  iobdetails: 'docs/user-guides/iob-details.html',
  surveytransferquestionrulesdetails: 'docs/survey-transfer-mapping-guide.html',
  surveytransferansweroptionrulesdetails: 'docs/survey-transfer-mapping-guide.html',
  // The pricing pages have their own dedicated guide, separate from Opportunities.
  sales_pricing: 'docs/user-guides/pricing.html',
  sales_pricingdetaillist: 'docs/user-guides/pricing.html',
  pricingitemdetails: 'docs/user-guides/pricing.html',
  creditmemodetails: 'docs/user-guides/credit-memo-details.html',
  creditmemorequestdetails: 'docs/user-guides/credit-memo-request-details.html',
  tpcreditmemodetails: 'docs/user-guides/tp-credit-memos.html',
  clientinvoicedetails: 'docs/user-guides/client-invoices.html',
  tpclientinvoicedetails: 'docs/user-guides/tp-client-invoices.html',
  paymentdetails: 'docs/user-guides/assignment-payment-details.html',
  servicedetails: 'docs/user-guides/services.html',
  fraudhitdetails: 'docs/user-guides/anti-fraud-tool.html',
  clientpaymentdetails: 'docs/user-guides/client-payments.html',
  new_clientpaymentdetails: 'docs/user-guides/client-payments-new.html',
  clientprepaymentdetails: 'docs/user-guides/client-payments.html',
  tprelatedclientprepaymentdetails: 'docs/user-guides/client-payments-new.html',
  tpclientprepaymentdetails: 'docs/user-guides/client-payments-new.html',
  // The finance report pages, each with a guide of its own. These entries are checked before the generic
  // reports_ prefix rule, which sends every other report page to the overview.
  reports_unbilledar: 'docs/user-guides/unbilled-ar.html',
  reports_lineitemsar: 'docs/user-guides/line-items-ar.html',
  reports_prepaylineitemsar: 'docs/user-guides/prepay-line-items-ar.html',
  reports_creditmemosar: 'docs/user-guides/credit-memos-ar.html',
  reports_prepaycreditmemosar: 'docs/user-guides/prepay-credit-memos-ar.html',
  reports_clientdepositdetails: 'docs/user-guides/client-deposit-details.html',
  reports_voidandreissued: 'docs/user-guides/void-and-reissued.html',
  reports_assignmentdetails: 'docs/user-guides/assignment-details.html',
  reports_assignmentdetailslite: 'docs/user-guides/assignment-details-lite.html',
  reports_tpassignmentdetails: 'docs/user-guides/third-party-assignment-details.html',
  reports_clientassignmentrevenueexpenses: 'docs/user-guides/client-assignment-revenue-expenses-summary.html',
  reports_projectassignmentrevenueexpenses: 'docs/user-guides/project-assignment-revenue-expenses-summary.html',
  reports_clientadjustedgrossrevenue: 'docs/user-guides/client-adjusted-gross-revenue.html',
  reports_officegrossprofitreport: 'docs/user-guides/office-gross-profit.html',
  reports_evaluatorexpenseanalysisreport: 'docs/user-guides/evaluator-expense-analysis.html',
  reports_operationalsubledgerdetail: 'docs/user-guides/operational-subledger-detail.html',
  reports_operationalsubledgersummary: 'docs/user-guides/operational-subledger-summary.html',
  reports_journalentriesreport: 'docs/user-guides/journal-entries.html',
  reports_ioblist: 'docs/user-guides/iob-list.html',
  reports_arageingreport: 'docs/user-guides/ar-ageing-report.html',
  reports_iobsummary: 'docs/user-guides/iob-summary.html',
  reports_taxreport: 'docs/user-guides/tax-report.html',
  reports_evaluatorpaymentrec: 'docs/user-guides/evaluator-payment-rec-report.html',
  reports_assignmentfeerevenuebyindustry: 'docs/user-guides/assignment-fee-revenue-by-industry.html',
  reports_assignmentfeerevenuebyservice: 'docs/user-guides/assignment-fee-revenue-by-service.html',
  reports_lineitemfeerevenuebyindustry: 'docs/user-guides/line-item-fee-revenue-by-industry.html',
  reports_lineitemfeerevenuebytype: 'docs/user-guides/line-item-fee-revenue-by-type.html',
  reports_assignmentslineitemsfeerevenuebyindustry:
    'docs/user-guides/assignments-line-items-fee-revenue-by-industry.html',
};

// A report page without an entry above opens the one reports-area overview guide; report pages share
// the `reports_` url prefix (the same convention redux/selectors.js uses for the eyebrow).
const REPORTS_URL_PREFIX = 'reports_';
const REPORTS_OVERVIEW_GUIDE = 'docs/user-guides/reports.html';

// Depth-first sweep of the (nested) menu tree collecting every item matching the predicate.
const collectMenuItems = (items, predicate, found = []) => {
  if (!Array.isArray(items)) {
    return found;
  }
  for (const item of items) {
    if (predicate(item)) {
      found.push(item);
    }
    if (item.submenu) {
      collectMenuItems(item.submenu, predicate, found);
    }
  }
  return found;
};

const guideForEntry = (entry) => {
  if (!entry) {
    return undefined;
  }
  if (USER_GUIDES[entry.text]) {
    return USER_GUIDES[entry.text];
  }
  const entryUrl = (entry.url || '').toLowerCase();
  if (USER_GUIDES_BY_URL[entryUrl]) {
    return USER_GUIDES_BY_URL[entryUrl];
  }
  if (entryUrl.indexOf(REPORTS_URL_PREFIX) === 0) {
    return REPORTS_OVERVIEW_GUIDE;
  }
  return undefined;
};

// Returns the full URL of the guide for the current page, or null when the page has no guide.
// Accepts { title, url }. Neither is unique on its own (display labels collide, e.g. 'Invoices';
// url slugs collide too, e.g. Leads/Contacts both use 'sales'), so the entry is resolved by
// title first and the url only disambiguates — except for url-only pages with dynamic titles,
// where a unique url match identifies the entry by itself. The url may carry a '/search' or
// query suffix from in-app navigation; only its base slug is compared, case-insensitively.
export function getUserGuidePath({ title, url } = {}) {
  const baseUrl = url ? url.split(/[/?]/)[0].toLowerCase() : '';
  let entry;
  if (title) {
    const candidates = collectMenuItems(menuitems, (item) => item.text === title || item.linktext === title);
    entry =
      (candidates.length > 1 && baseUrl && candidates.find((item) => (item.url || '').toLowerCase() === baseUrl)) ||
      candidates[0];
  }
  if (!entry && baseUrl) {
    const byUrl = collectMenuItems(menuitems, (item) => (item.url || '').toLowerCase() === baseUrl);
    if (byUrl.length === 1) {
      entry = byUrl[0];
    }
  }
  const relativePath = guideForEntry(entry) || (title && USER_GUIDES[title]) || undefined;
  return relativePath ? `${process.env.PUBLIC_URL}/${relativePath}` : null;
}

export default USER_GUIDES;
